/**
 * The shape of a hand (H2).
 *
 * One row, always. A hand that wrapped was the old answer and it was wrong:
 * thirteen cards in two rows reads as two hands, and the row you are not
 * looking at is the one holding the card you want.
 *
 * ## What is actually held constant
 *
 * Not the card width. The earlier version held width at a 40px floor and
 * wrapped when it ran out, which is backwards - you never read a whole card in
 * a fan, you read the corner. So the thing to protect is the **sliver**: how
 * much of each card shows past the one in front of it.
 *
 * Both width and overlap flex to keep that true. Few cards: wide, barely
 * overlapping. Many: narrower and packed, with the index still clear. That is
 * what a hand of cards does when you hold it, and it is what the mockups draw -
 * measured off one, thirteen cards across a 390pt phone are about 41pt wide
 * showing about 26pt each.
 *
 * ## Why this is arithmetic and not CSS
 *
 * The stylesheet used to compute the card width from container-query units
 * while this file computed the wrap point, and the two agreed only because
 * comments in both said they had to. Every number now comes from here and
 * arrives as a custom property, so there is one source and nothing to keep in
 * step.
 */

/**
 * The share of the container the fan may use.
 *
 * The missing 3% keeps the outermost card off the edge of the screen, where a
 * rounded corner or a gesture area would clip it.
 */
const FAN_FILL = 0.97;

/**
 * How much of a card should show, as a share of its width, when there is room.
 *
 * 0.63 is measured off the mockup rather than chosen. It is a target, not a
 * floor: a hand small enough to be roomier gets wider cards instead, and a
 * hand too big for it packs tighter.
 */
const PREFERRED_VISIBLE = 0.63;

/**
 * The most of a card that may show, however much room there is.
 *
 * Without this the leftover room is shared out until `step` reaches a whole
 * card width - so seven cards on a desktop came out as seven separate cards in
 * a row, touching but not overlapping, which is not what a held hand looks
 * like.
 *
 * 0.8 is where this started: the version before H2 overlapped every card by a
 * fixed 20%, and that was never the complaint. What H2 got wrong was applying
 * the *tight* share at every size, so a hand with room to spare still hid a
 * third of every card behind the next one.
 */
const MAX_VISIBLE = 0.8;

/** Never wider than this, however few cards are held. */
const MAX_CARD_PX = 92;

/**
 * And never more than this share of the row it sits in.
 *
 * `MAX_CARD_PX` is a flat ceiling, so a small hand hit it at every width - a
 * four-card hand drew 92px cards on a 320px phone exactly as it did on a
 * 2560px desktop, which is 31% of the row there against 25% on a larger phone
 * and a rounding error on a monitor. The screen that can least afford the
 * height was giving up the most of it.
 *
 * 0.28 is chosen to bite there and nowhere else: the hand measures 296px at
 * 320 and 336px at 360, so this caps the first at 83px and leaves every size
 * above it on the flat ceiling, untouched.
 */
const MAX_CARD_SHARE = 0.28;

/**
 * Never narrower than this.
 *
 * Below about thirty pixels the rank in the corner stops being legible at arm's
 * length, and a hand you cannot read is not a hand. A count large enough to
 * need narrower cards than this packs the overlap tighter instead.
 */
const MIN_CARD_PX = 30;

export interface FanGeometry {
  /** Card width in px. */
  cardW: number;
  /** Distance from one card's left edge to the next: the visible sliver. */
  step: number;
  /**
   * The circle the card centres sit on, measured in card-widths.
   *
   * This is what makes the fan an arc rather than a row of tilted cards. It is
   * not a free choice: the angles and the spacing already fix it. Neighbouring
   * cards are `step` apart and differ by one `delta` of rotation, so the
   * radius that puts both of those on the same circle is `step / delta`.
   */
  radius: number;
}

/**
 * How wide each card is, and how far apart they sit.
 *
 * A container width of zero means "not measured yet". The answer there is the
 * widest a card is allowed to be: the fan is about to be measured and
 * re-rendered, and starting big and settling smaller is less visible than the
 * reverse.
 */
export function fanGeometry(count: number, containerPx: number): FanGeometry {
  const cards = Math.max(1, Math.trunc(count));
  if (containerPx <= 0) {
    return {
      cardW: MAX_CARD_PX,
      step: MAX_CARD_PX * PREFERRED_VISIBLE,
      radius: arcRadius(cards, PREFERRED_VISIBLE),
    };
  }

  const room = containerPx * FAN_FILL;
  const gaps = cards - 1;
  const tilt = tiltWidth(cards);

  /*
   * The width at which every card shows its preferred share.
   *
   * `gaps * ratio + 1` is the row's width in card-widths: every card but the
   * last contributes its visible sliver, and the last contributes all of
   * itself. `+ tilt` is the pair of corners the end cards swing outside that
   * row when they rotate - without it the arithmetic solves for a row of
   * upright cards and the drawn fan is wider than the answer it was given.
   *
   * It stays a closed form because the overhang is proportional to the card
   * width: extent = cardW * (gaps * ratio + 1 + tilt), so solving for cardW is
   * still one division.
   */
  const ideal = room / (gaps * PREFERRED_VISIBLE + 1 + tilt);
  const ceiling = Math.min(MAX_CARD_PX, containerPx * MAX_CARD_SHARE);
  const cardW = Math.min(ceiling, Math.max(MIN_CARD_PX, ideal));

  /*
   * What is left over, shared out - and never more than `MAX_VISIBLE`, so a
   * hand with room to spare fans out to its comfortable overlap and stops
   * rather than spreading into a row of separate cards.
   *
   * When the cards hit their minimum width this comes out smaller than the
   * preferred share, which is the intended give: the hand packs tighter rather
   * than wrapping or shrinking past readable.
   */
  /*
   * A pair is not a fan, and overlapping it hides half of one card.
   *
   * `MAX_VISIBLE` keeps a HAND looking held rather than spread into a row, and
   * that is right from three cards up. At two it buys nothing and costs
   * everything: there is room for both on any phone, so a fifth of one card
   * was being tucked behind the other for the look of the thing. In Hold'em
   * that is one of the two cards you are given all hand, and players reported
   * not being able to read it.
   */
  const overlapCap = cards <= 2 ? cardW : cardW * MAX_VISIBLE;

  const step = gaps === 0 ? cardW : Math.min(overlapCap, (room - cardW * (1 + tilt)) / gaps);

  const settled = Math.max(1, step);
  return { cardW, step: settled, radius: arcRadius(cards, settled / cardW) };
}

/** Degrees from the leftmost card to the rightmost. */
function spreadOf(count: number): number {
  return Math.min(26, count * 3.2);
}

/**
 * The arc the cards sit on, in card-widths.
 *
 * `visible` is the step as a share of a card width, so `visible / delta` is the
 * radius in card-widths - the circle on which cards one `delta` apart end up
 * one `step` apart.
 *
 * A single card has no neighbour to be spaced from and no arc to sit on.
 */
function arcRadius(count: number, visible: number): number {
  if (count <= 1) return 0;
  const delta = (spreadOf(count) * Math.PI) / 180 / (count - 1);
  return visible / delta;
}

/**
 * How far past the row's own width the tilted end cards reach, in card-widths.
 *
 * The horizontal twin of `fanHeadroom`, and the same arithmetic: a card
 * rotated by θ occupies `w·cosθ + h·sinθ`, and 1.4 is the card's aspect, so
 * the pair of them together reach `cosθ + 1.4·sinθ - 1` card-widths wider than
 * they would standing upright.
 *
 * This is what the fit calculation was missing. `FAN_FILL` left 3% of the
 * container spare, which is nowhere near enough: at six cards the overhang is
 * about 22% of a card width, so the hand overran its container and the scroll
 * box it sits in grew a scrollbar.
 */
function tiltWidth(count: number): number {
  // The outermost card's tilt, which is half the spread. Taken from `spreadOf`
  // rather than from `arcOf`, because the angle is all this needs and `arcOf`
  // would want a radius that the width calculation has not worked out yet.
  if (count <= 1) return 0;
  const radians = (spreadOf(count) * Math.PI) / 360;
  return Math.cos(radians) + 1.4 * Math.sin(radians) - 1;
}

/**
 * How wide the fan actually draws, tilt included.
 *
 * Exported so the test measures the thing on screen rather than the row the
 * cards would make if none of them were rotated - which is the gap that let a
 * passing suite sit beside a hand hanging off both edges of a phone.
 */
export function fanExtent(count: number, geometry: FanGeometry): number {
  const gaps = Math.max(0, count - 1);
  return gaps * geometry.step + geometry.cardW * (1 + tiltWidth(count));
}

export interface ArcPlacement {
  /** Degrees, negative to the left of centre. */
  angle: number;
  /** How far the card drops below the row's high point, as a share of a card width. */
  drop: number;
}

/**
 * Where one card sits on the arc.
 *
 * The spread grows with the number of cards but stops: past about eight, adding
 * more should make the fan denser rather than more curved, or the outer cards
 * end up lying on their sides.
 *
 * ## The drop comes from the arc now, and used to be invented
 *
 * It was `t * t * 0.1` - the right SHAPE, since `1 - cos` is quadratic near
 * zero, but a coefficient with nothing behind it. The angles described one arc
 * and the drop described another, so the outer cards were tilted as though
 * they lay on a curve and then never lowered onto it. Seven cards on a desktop
 * left the end ones about 12px high, which reads as those cards standing
 * proud of the fan rather than sitting in it.
 *
 * `radius * (1 - cos angle)` is how far a point on a circle of that radius has
 * fallen by the time it has swung through that angle. Same circle as the
 * angles, so the card lands on the curve.
 *
 * A single card gets no tilt at all: an arc of one is just a crooked card.
 */
export function arcOf(index: number, count: number, radius: number): ArcPlacement {
  if (count <= 1) return { angle: 0, drop: 0 };

  const mid = (count - 1) / 2;
  // -1 at the left edge, 0 in the middle, +1 at the right.
  const t = (index - mid) / mid;

  const angle = (t * spreadOf(count)) / 2;
  const drop = radius * (1 - Math.cos((Math.abs(angle) * Math.PI) / 180));
  return { angle, drop };
}

/**
 * The headroom the fan needs BELOW the row, as a share of a card width.
 *
 * A rotated card reaches past the box its upright self occupied, and the hand
 * sits in a scroll container that clips anything outside its padding box - the
 * raised edge of a playable card was being sliced off along the top before
 * there was any rotation at all.
 *
 * Two things reach down here and only one reaches up, which is why there are
 * two functions. The tilt swings a corner out of line in both directions; the
 * arc's `drop` is one-way, because a fan drops its outer cards and never
 * raises them.
 */
export function fanHeadroom(count: number, radius: number): number {
  const { angle, drop } = arcOf(0, count, radius);
  // Half the card's width swings out of line as it tilts; 1.4 is the card's
  // aspect, so half its height is 0.7 of a width.
  return tiltReach(angle) + drop;
}

/**
 * The headroom above the row, which is less.
 *
 * The same allowance used to be made at both ends, `drop` included - and the
 * drop above the cards is room nothing ever occupies. Measured on a phone the
 * topmost card began two pixels BELOW the row's own top edge while thirteen
 * pixels sat reserved above it, which on the screen reads as a gap between the
 * hand and the handle above it.
 */
export function fanHeadroomTop(count: number, radius: number): number {
  const { angle } = arcOf(0, count, radius);
  return tiltReach(angle);
}

/** How far a tilted card's corner rises out of the row, in card-widths. */
function tiltReach(angle: number): number {
  return 0.7 * Math.sin((Math.abs(angle) * Math.PI) / 180);
}

/**
 * `.zone-row`'s gap, in pixels.
 *
 * The stylesheet owns the other copy. Two numbers that must agree is the shape
 * of bug this codebase keeps finding, so the rule carries a comment pointing
 * here - but the honest fix would be a custom property, and this is the
 * cheaper half of it.
 */
export const ROW_GAP_PX = 6.4;

/**
 * The widest a card may be for a row of `count` to stay on one line.
 *
 * A board is five cards and must read as five cards. Wrapping the river onto a
 * second row splits the community into two groups that mean nothing - you read
 * a board left to right, and the fifth card is the one the whole hand turns
 * on.
 *
 * So the row shrinks its cards rather than breaking. Returns 0 when there is
 * nothing to decide yet - an unmeasured row, or an empty one - and the caller
 * leaves the ordinary width alone rather than clamping to a guess.
 *
 * The result is a CAP, not a size: it goes into a `min()` with the size the
 * card would have had, because five enormous cards on a laptop is not what a
 * board looks like either.
 */
export function fitRow(rowWidth: number, count: number, gap = ROW_GAP_PX): number {
  if (rowWidth <= 0 || count <= 0) return 0;
  const cap = Math.floor((rowWidth - gap * (count - 1)) / count);

  /*
   * Below a legible size, refuse to answer.
   *
   * Returning a very small number here is worse than returning nothing: the
   * caller puts it in a `min()`, and a board of five cards an inch wide is a
   * board of five cards nobody can see. It shipped exactly once, when the row
   * being measured was itself sized by the cards inside it - each pass made
   * the row narrower and the cards smaller until the flop was two pixels tall,
   * silently, with the game underneath entirely correct.
   *
   * The container width is what actually fixed that (see `.zone-row.one-line`).
   * This is the floor that stops the same shape of mistake from ever being
   * invisible again: cards keep their natural size and overflow the row, which
   * is wrong in a way somebody can SEE.
   */
  return cap < MIN_CARD_PX ? 0 : cap;
}
